import * as React from 'react'
require('../base.module.scss')
import { BaseComponentProps } from '../Common/extractClassNamesFromProps'
import { extractClassNamesFromProps } from '../Common/extractClassNamesFromProps'
import { Spinner } from '../Spinner/Spinner'

import * as st from './Icon.module.scss'
import * as otherStyles from './Other.module.scss'
import * as rrDeStyles from './RegulatoryRatingIconDE.module.scss'
import * as rrUkStyles from './RegulatoryRatingIconUK.module.scss'

interface IconProps extends BaseComponentProps {
  use: IconType,
  title?: string
}

export enum IconType {
  // General icons
  ADD = 'add',
  ALERT_CRITICAL = 'alert_critical',
  ALERT_CRITICAL_BLOCK = 'alert_critical_block',
  ALERT_CRITICAL_CLOSE = 'alert_critical_close',
  ALERT_INFO = 'alert_info',
  ALERT_INFO_BLOCK = 'alert_info_block',
  ALERT_INFO_CLOSE = 'alert_info_close',
  ALERT_PRIME_CLOSE = 'alert_prime_close',
  ALERT_PRIME_SUCCESS = 'alert_prime_success',
  ALERT_SUCCESS = 'alert_success',
  ALERT_SUCCESS_BLOCK = 'alert_success_block',
  ALERT_SUCCESS_CLOSE = 'alert_success_close',
  ALERT_WARNING = 'alert_warning',
  ALERT_WARNING_BLOCK = 'alert_warning_block',
  ALERT_WARNING_CLOSE = 'alert_warning_close',
  AMAZON_RATING = 'amazon_rating',
  AUDIO_DESCRIPTION = 'audio_description',
  BREADCRUMB_CHEVRON = 'breadcrumb_chevron',
  CHAT = 'chat',
  CHATFLOW_CLOSE = 'chatflow_close',
  CHECKBOX = 'checkbox',
  CHECKBOX_CHECKED = 'checkbox_checked',
  CHECKBOX_CHECKED_FOCUS = 'checkbox_checked_focus',
  CHECKBOX_FOCUS = 'checkbox_focus',
  DISMISS_CLOSE_DARK = 'dismiss_close_dark',
  DISMISSABLE_CLOSE = 'dismissable_close',
  EDIT = 'edit',
  EMAIL = 'email',
  ENTITLED_TICK = 'entitled_tick',
  EXPANDER_DOWN = 'expander_down',
  EXPANDER_UP = 'expander_up',
  EXTERNAL = 'external',
  FACEBOOK = 'facebook',
  GRID = 'grid',
  HELP = 'help',
  LIST = 'list',
  LOCK = 'lock',
  LOGO_AMAZON = 'logo_amazon',
  LOGO_IMDB = 'logo_imdb',
  LOGO_PRIME = 'logo_prime',
  LOGO_PRIMEVIDEO = 'logo_primevideo',
  LOGO_PRIMEVIDEO_MONO = 'logo_primevideo_mono',
  OVERFLOW = 'overflow',
  PHONE = 'phone',
  PINTEREST = 'pinterest',
  PLAY_ARROW = 'play_arrow',
  PLAY_BUTTON_ARROW = 'play_button_arrow',
  PRIME_SASH = 'prime_sash',
  SEARCH = 'search',
  SEARCH_CLEAR_BUTTON = 'search_clear_button',
  SECOND_SCREEN_CLOSE = 'second_screen_close',
  SHARE = 'share',
  SMILE = 'smile',
  START_OVER = 'start_over',
  SUBTITLES = 'subtitles',
  SUBTITLES_BENTO = 'subtitles_bento',
  TICK = 'tick',
  TOGGLE_COLLAPSE = 'toggle_collapse',
  TOGGLE_EXPAND = 'toggle_expand',
  TRAILER = 'trailer',
  TWITTER = 'twitter',
  WATCHLIST_ON = 'watchlist_on',

  // Special Icon Spinner for Changing Button Status
  SPINNER = 'spinner',

  // Regulatory Rating Icons DE
  RR_DE_0 = 'rr_de_0',
  RR_DE_6 = 'rr_de_6',
  RR_DE_12 = 'rr_de_12',
  RR_DE_16 = 'rr_de_16',
  RR_DE_18 = 'rr_de_18',

  // Regulatory Rating Icons UK
  RR_UK_12 = 'rr_uk_12',
  RR_UK_15 = 'rr_uk_15',
  RR_UK_18 = 'rr_uk_18',
  RR_UK_PG = 'rr_uk_pg',
  RR_UK_U = 'rr_uk_u'
}

interface RegulatoryRatingIconTypes {
  [name: string]: {
    [name: string]: IconType
  }
}

const regulatoryRatingIconTypes: RegulatoryRatingIconTypes = {
  DE: {
    0: IconType.RR_DE_0,
    6: IconType.RR_DE_6,
    12: IconType.RR_DE_12,
    16: IconType.RR_DE_16,
    18: IconType.RR_DE_18
  },
  UK: {
    12: IconType.RR_UK_12,
    15: IconType.RR_UK_15,
    18: IconType.RR_UK_18,
    PG: IconType.RR_UK_PG,
    U: IconType.RR_UK_U
  }
}

export const getRegulatoryRatingIconType = (countryCode: string, simplifiedId: string): IconType =>
  (regulatoryRatingIconTypes[countryCode] || {})[simplifiedId]

/* eslint "dot-notation": 0 */
const classNames: Record<IconType, string[]> = {
  [IconType.ADD]: [st.icon, st['add']],
  [IconType.ALERT_CRITICAL_BLOCK]: [st.icon, st['alert-critical-block']],
  [IconType.ALERT_CRITICAL_CLOSE]: [st.icon, st['alert-critical-close']],
  [IconType.ALERT_CRITICAL]: [st.icon, st['alert-critical']],
  [IconType.ALERT_INFO_BLOCK]: [st.icon, st['alert-info-block']],
  [IconType.ALERT_INFO_CLOSE]: [st.icon, st['alert-info-close']],
  [IconType.ALERT_INFO]: [st.icon, st['alert-info']],
  [IconType.ALERT_PRIME_CLOSE]: [st.icon, st['alert-prime-close']],
  [IconType.ALERT_PRIME_SUCCESS]: [st.icon, st['alert-prime-success']],
  [IconType.ALERT_SUCCESS_BLOCK]: [st.icon, st['alert-success-block']],
  [IconType.ALERT_SUCCESS_CLOSE]: [st.icon, st['alert-success-close']],
  [IconType.ALERT_SUCCESS]: [st.icon, st['alert-success']],
  [IconType.ALERT_WARNING_BLOCK]: [st.icon, st['alert-warning-block']],
  [IconType.ALERT_WARNING_CLOSE]: [st.icon, st['alert-warning-close']],
  [IconType.ALERT_WARNING]: [st.icon, st['alert-warning']],
  [IconType.AMAZON_RATING]: [st.icon, st['amazon_rating']],
  [IconType.AUDIO_DESCRIPTION]: [st.icon, st['audio-description']],
  [IconType.BREADCRUMB_CHEVRON]: [st.icon, st['breadcrumb-chevron']],
  [IconType.CHAT]: [st.icon, st['chat']],
  [IconType.CHATFLOW_CLOSE]: [st.icon, st['chatflow-close']],
  [IconType.CHECKBOX_CHECKED_FOCUS]: [st.icon, st['checkbox-checked-focus']],
  [IconType.CHECKBOX_CHECKED]: [st.icon, st['checkbox-checked']],
  [IconType.CHECKBOX_FOCUS]: [st.icon, st['checkbox-focus']],
  [IconType.CHECKBOX]: [st.icon, st['checkbox']],
  [IconType.DISMISS_CLOSE_DARK]: [st.icon, st['dismiss-close-dark']],
  [IconType.DISMISSABLE_CLOSE]: [st.icon, st['dismissable-close']],
  [IconType.EDIT]: [st.icon, st['edit']],
  [IconType.EMAIL]: [st.icon, st['email']],
  [IconType.ENTITLED_TICK]: [st.icon, st['entitled-tick']],
  [IconType.EXPANDER_DOWN]: [st.icon, st['expander-down']],
  [IconType.EXPANDER_UP]: [st.icon, st['expander-up']],
  [IconType.EXTERNAL]: [st.icon, st['external']],
  [IconType.FACEBOOK]: [st.icon, st['facebook']],
  [IconType.GRID]: [st.icon, st['grid']],
  [IconType.HELP]: [st.icon, st['help']],
  [IconType.LIST]: [st.icon, st['list']],
  [IconType.LOCK]: [st.icon, st['lock']],
  [IconType.LOGO_AMAZON]: [st.icon, st['logo_amazon']],
  [IconType.LOGO_IMDB]: [st.icon, st['logo_imdb']],
  [IconType.LOGO_PRIME]: [st.icon, st['logo_prime']],
  [IconType.LOGO_PRIMEVIDEO_MONO]: [st.icon, st['logo_primevideo_mono']],
  [IconType.LOGO_PRIMEVIDEO]: [st.icon, st['logo_primevideo']],
  [IconType.OVERFLOW]: [st.icon, st['overflow']],
  [IconType.PHONE]: [st.icon, st['phone']],
  [IconType.PINTEREST]: [st.icon, st['pinterest']],
  [IconType.PLAY_ARROW]: [st.icon, st['play-arrow']],
  [IconType.PLAY_BUTTON_ARROW]: [st.icon, st['play-button-arrow']],
  [IconType.PRIME_SASH]: [st.icon, st['prime-sash']],
  [IconType.SEARCH_CLEAR_BUTTON]: [st.icon, st['search-clear-button']],
  [IconType.SEARCH]: [st.icon, st['search']],
  [IconType.SECOND_SCREEN_CLOSE]: [st.icon, st['second-screen-close']],
  [IconType.SHARE]: [st.icon, st['share']],
  [IconType.SMILE]: [st.icon, st['smile']],
  [IconType.START_OVER]: [st.icon, st['start-over']],
  [IconType.SUBTITLES_BENTO]: [st.icon, st['subtitles-bento']],
  [IconType.SUBTITLES]: [st.icon, st['subtitles']],
  [IconType.TICK]: [st.icon, st['tick']],
  [IconType.TOGGLE_COLLAPSE]: [st.icon, st['toggle-collpase']],
  [IconType.TOGGLE_EXPAND]: [st.icon, st['toggle-expand']],
  [IconType.TRAILER]: [st.icon, st['trailer']],
  [IconType.TWITTER]: [st.icon, st['twitter']],
  [IconType.WATCHLIST_ON]: [st.icon, st['watchlist-on']],

  [IconType.SPINNER]: [otherStyles.spinner],
  [IconType.RR_DE_0]: [rrDeStyles.icon, rrDeStyles['rating-0']],
  [IconType.RR_DE_6]: [rrDeStyles.icon, rrDeStyles['rating-6']],
  [IconType.RR_DE_12]: [rrDeStyles.icon, rrDeStyles['rating-12']],
  [IconType.RR_DE_16]: [rrDeStyles.icon, rrDeStyles['rating-16']],
  [IconType.RR_DE_18]: [rrDeStyles.icon, rrDeStyles['rating-18']],
  [IconType.RR_UK_12]: [rrUkStyles.icon, rrUkStyles['rating-12']],
  [IconType.RR_UK_15]: [rrUkStyles.icon, rrUkStyles['rating-15']],
  [IconType.RR_UK_18]: [rrUkStyles.icon, rrUkStyles['rating-18']],
  [IconType.RR_UK_PG]: [rrUkStyles.icon, rrUkStyles['rating-pg']],
  [IconType.RR_UK_U]: [rrUkStyles.icon, rrUkStyles['rating-u']]
}

export const Icon: React.ComponentType<IconProps> = (props: IconProps) => {
  if (props.use === IconType.SPINNER) {
    const className = [props.className, ...classNames[IconType.SPINNER]].join(' ')
    return <span className={className}><Spinner fillHeight /></span>
  }

  const className = extractClassNamesFromProps(props, ...classNames[props.use])

  return <span className={className} title={props.title} />
}

// Add displayName to preserve the component name on snapshot testing
Icon.displayName = 'Icon'

